import * as braintree from "braintree-web";
import paypal from "paypal-checkout";
import * as $ from "jquery";
import {PayPalCheckoutCreatePaymentOptions} from "braintree-web/modules/paypal-checkout";
import createRequest from "../utils/create-request";

interface PayPalUrls {
    authorize: string;
    review: string;
    cleanup: string;
}

interface PayPalCallbacks {
    payment?: (config: PayPalConfig) => void;
    authorize?: {
        start?: (config: PayPalConfig) => void,
        fail?: (data, config: PayPalConfig) => void,
        success?: (data, config: PayPalConfig) => void,
        error?: (reason, config: PayPalConfig) => void
    };
    cancel?: (data, config: PayPalConfig) => void,
    error?: (err, config: PayPalConfig) => void,
}

export interface PayPalConfig {
    urls: PayPalUrls;
    callbacks?: PayPalCallbacks;
    paypalAccessToken: string;
    env: string;
    review: boolean;
    paymentsOptions: PayPalCheckoutCreatePaymentOptions;
}

export class PayPal {
    private readonly config: PayPalConfig;
    private data?: {
        fundID: number,
        price: number,
        sourceCode: number,
    };

    constructor(config: PayPalConfig) {
        this.config = config;
        PayPal.log("Init", this.config)
    }

    public isEnabled(): boolean {
        return this.data.price >= 0 && !isNaN(this.data.price);
    }

    public setData(data) {
        this.data = data;
    }

    public init() {
        braintree.client.create({
            authorization: this.config.paypalAccessToken
        }, (clientErr, clientInstance) => {
            // Stop if there was a problem creating the client.
            // This could happen if there is a network error or if the authorization
            // is invalid.
            if (clientErr) {
                PayPal.log('Error creating client', clientErr);
                return;
            }

            // Create a PayPal Checkout component.
            return braintree.paypalCheckout.create({
                client: clientInstance
            }, (paypalCheckoutErr, paypalCheckoutInstance) => {
                // Stop if there was a problem creating PayPal Checkout.
                // This could happen if there was a network error or if it's incorrectly
                // configured.
                if (paypalCheckoutErr) {
                    PayPal.log('Error creating PayPal Checkout', paypalCheckoutErr);
                    return;
                }

                // Set up PayPal with the checkout.js library
                return paypal.Button.render({
                    env: this.config.env,
                    commit: !this.config.review,
                    style: {
                        size: 'responsive',
                        height: 40,
                        color: 'white',
                        shape: 'rect',
                        label: 'paypal',
                        tagline: false,
                        flow: 'checkout'
                    },

                    validate: (actions) => {
                        this.validateActions(actions);

                        $('#paypal-button').on("change", () => {
                            this.validateActions(actions);
                        });
                    },

                    payment: () => {
                        this.config.callbacks && this.config.callbacks.payment && this.config.callbacks.payment(this.config);

                        let createParams: PayPalCheckoutCreatePaymentOptions = this.config.paymentsOptions;
                        createParams.amount = this.data.price;

                        return paypalCheckoutInstance.createPayment(createParams);
                    },

                    onAuthorize: (data, actions) => {
                        this.config.callbacks && this.config.callbacks.authorize && this.config.callbacks.authorize.start && this.config.callbacks.authorize.start(this.config);

                        return paypalCheckoutInstance.tokenizePayment(data)
                            .then((payload) => {
                                PayPal.log("Payment Authorized");
                                $('#paypal-overlay').show();
                                createRequest(this.config.urls.authorize, {
                                    'data': this.data,
                                    'payload': payload
                                })
                                    .then((data: {
                                        status: string,
                                        redirect_url: string
                                    }) => {
                                        if (typeof data.status !== 'undefined' && data.status == 'success') {
                                            PayPal.log("Payment Authorized: Success")
                                            this.config.callbacks && this.config.callbacks.authorize && this.config.callbacks.authorize.success && this.config.callbacks.authorize.success(data, this.config)
                                        } else {
                                            PayPal.log("Payment Authorized: Fail")
                                            this.config.callbacks && this.config.callbacks.authorize && this.config.callbacks.authorize.fail && this.config.callbacks.authorize.fail(data, this.config)
                                        }
                                    })
                                    .catch((reason) => {
                                        PayPal.log("Payment Authorized: Error", reason)
                                        this.config.callbacks && this.config.callbacks.authorize && this.config.callbacks.authorize.error && this.config.callbacks.authorize.error(reason, this.config)
                                    });
                            });
                    },

                    onCancel: (data) => {
                        PayPal.log("Payment Cancelled", data);
                        this.config.callbacks && this.config.callbacks.cancel && this.config.callbacks.cancel(data, this.config);
                    },

                    onError: (err) => {
                        PayPal.log("Error", err);
                        this.config.callbacks && this.config.callbacks.error && this.config.callbacks.error(err, this.config);
                    }
                }, '#paypal-button').then(function () {

                });
            });
        });
    }

    public validateActions(actions): void {
        if (!this.isEnabled()) {
            actions.disable();
        } else {
            actions.enable();
        }
    }

    private static log(message?: string, ...optionalParams: any[]): void {
        console.log(`PayPal: ${message}`, ...optionalParams)
    }
}



// WEBPACK FOOTER //
// ./@modules/paypal/paypal.ts